import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
 Plus,
 MessageSquare,
 Send,
 ArrowLeft,
 ListChecks,
 Loader2,
} from 'lucide-react';
import { SearchSelect } from '../../components/shared';
import {
 getVendorSupportTickets,
 getVendorSupportTicket,
 createVendorSupportTicket,
 replyToVendorSupportTicket,
} from '../../services/supportTicketService';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { useToastContext } from '../../contexts/ToastContext';
import { VendorPageHeader, VendorGuideCallout } from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';

const PRIORITY_OPTIONS = [
 { value: 'low', label: 'Low' },
 { value: 'medium', label: 'Medium' },
 { value: 'high', label: 'High' },
 { value: 'urgent', label: 'Urgent' },
];

const CATEGORY_OPTIONS = [
 { value: 'general', label: 'General' },
 { value: 'technical', label: 'Technical' },
 { value: 'billing', label: 'Billing' },
 { value: 'feature_request', label: 'Feature Request' },
 { value: 'bug_report', label: 'Bug Report' },
];

const getStatusColor = (status) => {
 const config = {
 open: 'bg-surface-muted text-ink border-border',
 in_progress:
 'bg-amber-50 text-amber-800 border-amber-200',
 resolved:
 'bg-emerald-50 text-emerald-800 border-emerald-200',
 closed:
 'bg-gray-100 text-gray-700 border-gray-200',
 };
 return (
 config[status] ||
 'bg-gray-100 text-gray-700 border-gray-200'
 );
};

const getPriorityColor = (priority) => {
 const config = {
 low: 'bg-emerald-50 text-emerald-800 border-emerald-200',
 medium:
 'bg-amber-50 text-amber-800 border-amber-200',
 high: 'bg-orange-50 text-orange-800 border-orange-200',
 urgent:
 'bg-red-50 text-red-700 border-red-200',
 };
 return (
 config[priority] ||
 'bg-gray-100 text-gray-700 border-gray-200'
 );
};

const inputClassName =
 'w-full px-3 py-2 border border-gray-300 rounded-lg bg-surface text-ink placeholder:text-ink-tertiary focus:ring-2 focus:ring-ink focus:border-transparent';

const labelClassName = 'block text-sm font-medium text-ink-secondary mb-1';

const VendorPrioritySupport = () => {
 const queryClient = useQueryClient();
 const { success, error: showError } = useToastContext();
 const [view, setView] = useState('list'); // 'list' | 'create' | 'detail'
 const [selectedTicketId, setSelectedTicketId] = useState(null);
 const [createForm, setCreateForm] = useState({
 subject: '',
 description: '',
 priority: 'medium',
 category: 'general',
 });
 const [replyText, setReplyText] = useState('');

 const { data: ticketsResponse, isLoading } = useQuery({
 queryKey: ['vendorSupportTickets'],
 queryFn: () => getVendorSupportTickets({ per_page: 50 }),
 });

 const { data: ticketResponse, isLoading: ticketLoading } = useQuery({
 queryKey: ['vendorSupportTicket', selectedTicketId],
 queryFn: () => getVendorSupportTicket(selectedTicketId),
 enabled: !!selectedTicketId && view === 'detail',
 });

 const createMutation = useMutation({
 mutationFn: createVendorSupportTicket,
 onSuccess: (data) => {
 queryClient.invalidateQueries(['vendorSupportTickets']);
 setView('detail');
 setSelectedTicketId(data?.data?.id);
 setCreateForm({ subject: '', description: '', priority: 'medium', category: 'general' });
 success('Support ticket created successfully');
 },
 onError: (err) => showError(err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to create ticket'),
 });

 const replyMutation = useMutation({
 mutationFn: ({ id, data }) => replyToVendorSupportTicket(id, data),
 onSuccess: () => {
 setReplyText('');
 queryClient.invalidateQueries(['vendorSupportTicket', selectedTicketId]);
 queryClient.invalidateQueries(['vendorSupportTickets']);
 success('Reply sent successfully');
 },
 onError: (err) => showError(err?.response?.data?.message || 'Failed to send reply'),
 });

 const tickets = ticketsResponse?.data?.data || ticketsResponse?.data || [];
 const ticketsList = Array.isArray(tickets) ? tickets : tickets?.data || [];
 const ticket = ticketResponse?.data;

 const handleCreateTicket = (e) => {
 e.preventDefault();
 if (!createForm.subject.trim() || !createForm.description.trim()) return;
 createMutation.mutate(createForm);
 };

 const handleReply = (e) => {
 e.preventDefault();
 if (!replyText.trim()) return;
 replyMutation.mutate({ id: selectedTicketId, data: { response: replyText.trim() } });
 };

 const openDetail = (t) => {
 setSelectedTicketId(t.id);
 setView('detail');
 };

 return (
 <div className="space-y-5">
 <VendorPageHeader
 title="Priority Support"
 subtitle="Create and manage support tickets. Our team typically responds within 24 hours."
 actions={
 view === 'list' ? (
 <button
 type="button"
 onClick={() => setView('create')}
 className="inline-flex items-center gap-2 px-4 py-2 bg-ink text-white rounded-md hover:bg-black transition-colors"
 >
 <Plus className="w-5 h-5" />
 New Ticket
 </button>
 ) : null
 }
 />

 <VendorGuideCallout
 title={VENDOR_PAGE_GUIDES.prioritySupport.title}
 steps={VENDOR_PAGE_GUIDES.prioritySupport.steps}
 icon={ListChecks}
 />

 {view === 'list' && (
 <div className="product-panel overflow-hidden">
 {isLoading ? (
 <div className="py-20 text-center">
 <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto mb-3" />
 <p className="text-sm text-ink-tertiary">Loading tickets…</p>
 </div>
 ) : ticketsList.length === 0 ? (
 <div className="py-16 sm:py-20 text-center px-4">
 <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-lg border border-border bg-surface-muted text-gray-400">
 <MessageSquare className="w-7 h-7" />
 </div>
 <h3 className="text-base font-semibold text-ink">
 No support tickets yet
 </h3>
 <p className="mt-1.5 text-sm text-ink-tertiary max-w-sm mx-auto leading-relaxed">
 Open a ticket and our team typically responds within 24 hours.
 </p>
 <button
 type="button"
 onClick={() => setView('create')}
 className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-ink text-white rounded-md hover:bg-black transition-colors"
 >
 <Plus className="w-4 h-4" />
 Create your first ticket
 </button>
 </div>
 ) : (
 <div className="divide-y divide-border">
 {ticketsList.map((t) => (
 <div
 key={t.id}
 onClick={() => openDetail(t)}
 className="p-4 sm:p-5 hover:bg-surface-muted/50 cursor-pointer flex items-center justify-between gap-4 transition-colors"
 >
 <div className="min-w-0 flex-1">
 <p className="font-medium text-ink truncate">{t.subject}</p>
 <p className="text-sm text-ink-tertiary truncate mt-0.5">
 {t.description}
 </p>
 <div className="flex flex-wrap gap-2 mt-2">
 <span
 className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium capitalize border ${getStatusColor(t.status)}`}
 >
 {t.status?.replace('_', ' ')}
 </span>
 <span
 className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium capitalize border ${getPriorityColor(t.priority)}`}
 >
 {t.priority}
 </span>
 </div>
 </div>
 <div className="ml-4 text-right flex-shrink-0">
 <p className="text-sm text-ink-tertiary">
 {formatDate(t.created_at, 'medium')}
 </p>
 </div>
 </div>
 ))}
 </div>
 )}
 </div>
 )}

 {view === 'create' && (
 <div className="product-panel overflow-hidden p-5 sm:p-6">
 <button
 type="button"
 onClick={() => setView('list')}
 className="flex items-center gap-2 text-ink-secondary hover:text-gray-900 mb-4 transition-colors"
 >
 <ArrowLeft className="w-4 h-4" /> Back
 </button>
 <h2 className="text-lg font-semibold text-ink mb-4">
 Create Support Ticket
 </h2>
 <form onSubmit={handleCreateTicket} className="space-y-4">
 <div>
 <label className={labelClassName}>Subject *</label>
 <input
 type="text"
 value={createForm.subject}
 onChange={(e) => setCreateForm({ ...createForm, subject: e.target.value })}
 placeholder="Brief description of your issue"
 className={inputClassName}
 required
 />
 </div>
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
 <div>
 <label className={labelClassName}>Category</label>
 <SearchSelect
 value={createForm.category}
 onChange={(v) => setCreateForm({ ...createForm, category: v })}
 options={CATEGORY_OPTIONS}
 />
 </div>
 <div>
 <label className={labelClassName}>Priority</label>
 <SearchSelect
 value={createForm.priority}
 onChange={(v) => setCreateForm({ ...createForm, priority: v })}
 options={PRIORITY_OPTIONS}
 />
 </div>
 </div>
 <div>
 <label className={labelClassName}>Description *</label>
 <textarea
 value={createForm.description}
 onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
 placeholder="Provide as much detail as possible..."
 rows={5}
 className={inputClassName}
 required
 />
 </div>
 <div className="flex flex-wrap gap-3">
 <button
 type="submit"
 disabled={createMutation.isPending}
 className="px-4 py-2 bg-ink text-white rounded-md hover:bg-black disabled:opacity-50 transition-colors"
 >
 {createMutation.isPending ? 'Creating...' : 'Create Ticket'}
 </button>
 <button
 type="button"
 onClick={() => setView('list')}
 className="px-4 py-2 border border-border text-gray-800 rounded-md hover:bg-surface-muted transition-colors"
 >
 Cancel
 </button>
 </div>
 </form>
 </div>
 )}

 {view === 'detail' && ticket && (
 <div className="space-y-4">
 <button
 type="button"
 onClick={() => {
 setView('list');
 setSelectedTicketId(null);
 }}
 className="flex items-center gap-2 text-ink-secondary hover:text-gray-900 transition-colors"
 >
 <ArrowLeft className="w-4 h-4" /> Back to tickets
 </button>

 <div className="product-panel overflow-hidden p-5 sm:p-6">
 {ticketLoading ? (
 <div className="py-16 text-center">
 <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto mb-3" />
 <p className="text-sm text-ink-tertiary">Loading ticket…</p>
 </div>
 ) : (
 <>
 <div className="flex flex-wrap items-center gap-2 mb-4">
 <span
 className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium capitalize border ${getStatusColor(ticket.status)}`}
 >
 {ticket.status?.replace('_', ' ')}
 </span>
 <span
 className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium capitalize border ${getPriorityColor(ticket.priority)}`}
 >
 {ticket.priority}
 </span>
 <span className="text-sm text-ink-tertiary capitalize">
 {ticket.category}
 </span>
 </div>
 <h2 className="text-lg font-semibold text-ink mb-2">
 {ticket.subject}
 </h2>
 <p className="text-ink-secondary whitespace-pre-wrap mb-6">
 {ticket.description}
 </p>

 <div className="border-t border-border pt-6">
 <h3 className="font-medium text-ink mb-4">Conversation</h3>
 <div className="space-y-4 mb-6">
 {(ticket.ticket_replies || ticket.ticketReplies || []).map((reply) => (
 <div
 key={reply.id}
 className={`p-4 rounded-xl border-l-4 ${ reply.response_type === 'admin' ? 'bg-surface-muted/70 border-border ' : 'bg-surface-muted border-gray-300' }`}
 >
 <div className="flex items-center justify-between gap-3 mb-2">
 <span className="font-medium text-ink">
 {reply.response_type === 'admin' ? 'Support Team' : 'You'}
 </span>
 <span className="text-xs text-ink-tertiary shrink-0">
 {formatDate(reply.created_at, 'medium')} {formatTime(reply.created_at)}
 </span>
 </div>
 <p className="text-ink-secondary whitespace-pre-wrap">
 {reply.response_text || reply.response}
 </p>
 </div>
 ))}
 </div>

 {!['resolved', 'closed'].includes(ticket.status) && (
 <form onSubmit={handleReply} className="space-y-3">
 <textarea
 value={replyText}
 onChange={(e) => setReplyText(e.target.value)}
 placeholder="Type your reply..."
 rows={3}
 className={inputClassName}
 disabled={replyMutation.isPending}
 />
 <button
 type="submit"
 disabled={!replyText.trim() || replyMutation.isPending}
 className="inline-flex items-center gap-2 px-4 py-2 bg-ink text-white rounded-md hover:bg-black disabled:opacity-50 transition-colors"
 >
 <Send className="w-4 h-4" />
 {replyMutation.isPending ? 'Sending...' : 'Send Reply'}
 </button>
 </form>
 )}
 {['resolved', 'closed'].includes(ticket.status) && (
 <p className="text-sm text-ink-tertiary italic">
 This ticket is closed. Create a new ticket if you need further assistance.
 </p>
 )}
 </div>
 </>
 )}
 </div>
 </div>
 )}
 </div>
 );
};

export default VendorPrioritySupport;
